import { useCallback, useEffect, useRef } from "react";
import i18next from "i18next";
import { toast } from "sonner";

const CHECK_INTERVAL = 60_000;

let currentVersion: string | null = null;

async function fetchRemoteVersion(): Promise<string | null> {
  try {
    const res = await fetch(`/version.json?t=${Date.now()}`, {
      cache: "no-store",
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { version?: string };
    return data.version ?? null;
  } catch {
    return null;
  }
}

/**
 * 轮询 /version.json，检测到新版本时弹 toast 提示并 3 秒后自动刷新
 * 仅在 production 模式下生效
 */
export function useVersionCheck() {
  const toastIdRef = useRef<string | number | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const check = useCallback(async () => {
    const remote = await fetchRemoteVersion();
    if (!remote) return;

    if (currentVersion === null) {
      currentVersion = remote;
      return;
    }

    if (remote !== currentVersion && toastIdRef.current === null) {
      toastIdRef.current = toast.success(i18next.t("version_new_available"), {
        duration: 3000,
        position: "top-center",
      });
      setTimeout(() => window.location.reload(), 3000);
    }
  }, []);

  useEffect(() => {
    console.log("import.meta.env.DEV", import.meta.env.DEV);
    if (import.meta.env.DEV) return;

    void check();
    timerRef.current = setInterval(() => void check(), CHECK_INTERVAL);

    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        void check();
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [check]);
}
